import { useState, useCallback, useEffect } from 'react';

export type GitFileStatus = 'modified' | 'added' | 'deleted' | 'untracked' | 'renamed';

export type GitFile = {
  path: string;
  status: GitFileStatus;
  staged: boolean;
};

export type GitBranch = {
  name: string;
  current: boolean;
  remote?: string;
};

export type GitCommit = {
  hash: string;
  shortHash: string;
  message: string;
  author: string;
  email: string;
  date: string;
  parents: string[];
};

export type GitBlameLine = {
  lineNumber: number;
  hash: string;
  shortHash: string;
  author: string;
  date: string;
  message: string;
};

export type GitState = {
  files: GitFile[];
  branch: string;
  branches: GitBranch[];
  remotes: string[];
  ahead: number;
  behind: number;
  loading: boolean;
  error: string | null;
};

type UseGitParams = {
  apiBase: string;
  activeProject: string;
  token: string | null;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  workingFiles: Record<string, string>;
  persistedFiles: Record<string, string>;
};

// Compute local git status by comparing working files with persisted files
function computeLocalGitStatus(
  workingFiles: Record<string, string>,
  persistedFiles: Record<string, string>
): GitFile[] {
  const files: GitFile[] = [];
  const allPaths = Array.from(new Set([...Object.keys(workingFiles), ...Object.keys(persistedFiles)]));

  for (const path of allPaths) {
    const working = workingFiles[path];
    const persisted = persistedFiles[path];

    if (working !== undefined && persisted === undefined) {
      // New file
      files.push({ path, status: 'added', staged: false });
    } else if (working === undefined && persisted !== undefined) {
      // Deleted file
      files.push({ path, status: 'deleted', staged: false });
    } else if (working !== persisted) {
      // Modified file
      files.push({ path, status: 'modified', staged: false });
    }
  }

  return files.sort((a, b) => a.path.localeCompare(b.path));
}

export function useGit({
  apiBase,
  activeProject,
  token,
  authFetch,
  workingFiles,
  persistedFiles,
}: UseGitParams) {
  const [gitState, setGitState] = useState<GitState>({
    files: [],
    branch: 'main',
    branches: [],
    remotes: ['origin'],
    ahead: 0,
    behind: 0,
    loading: false,
    error: null,
  });

  const [isPushing, setIsPushing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [commitMessage, setCommitMessage] = useState('');
  const [isCommitting, setIsCommitting] = useState(false);
  const [commits, setCommits] = useState<GitCommit[]>([]);
  const [isLoadingCommits, setIsLoadingCommits] = useState(false);
  const [blameData, setBlameData] = useState<Record<string, GitBlameLine[]>>({});
  const [isLoadingBlame, setIsLoadingBlame] = useState(false);

  // Compute local changes based on working vs persisted files
  useEffect(() => {
    const localFiles = computeLocalGitStatus(workingFiles, persistedFiles);
    setGitState(prev => ({
      ...prev,
      files: localFiles,
    }));
  }, [workingFiles, persistedFiles]);

  // Fetch git status from server (if available)
  const fetchGitStatus = useCallback(async () => {
    if (!token || !apiBase) return;

    setGitState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/status`);
      if (response.ok) {
        const data = await response.json();
        setGitState(prev => ({
          ...prev,
          files: data.files || prev.files,
          branch: data.branch || prev.branch,
          branches: data.branches || prev.branches,
          remotes: data.remotes || prev.remotes,
          ahead: data.ahead || 0,
          behind: data.behind || 0,
          loading: false,
        }));
      } else {
        // Server doesn't support git status, use local computed status
        setGitState(prev => ({ ...prev, loading: false }));
      }
    } catch {
      // Use local computed status on error
      setGitState(prev => ({ ...prev, loading: false }));
    }
  }, [token, apiBase, activeProject, authFetch]);

  // Fetch branches from server
  const fetchBranches = useCallback(async () => {
    if (!token || !apiBase) return;

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/branches`);
      if (response.ok) {
        const data = await response.json();
        setGitState(prev => ({
          ...prev,
          branches: data.branches || [],
          remotes: data.remotes || ['origin'],
        }));
      }
    } catch {
      // Silently fail if branches endpoint not available
    }
  }, [token, apiBase, activeProject, authFetch]);

  // Fetch commit log from server
  const fetchCommitLog = useCallback(async (count = 50, path?: string): Promise<GitCommit[]> => {
    if (!token || !apiBase) {
      // Return seed data when API not available
      return generateSeedCommits();
    }

    setIsLoadingCommits(true);

    try {
      const params = new URLSearchParams({ count: String(count) });
      if (path) params.append('path', path);

      const response = await authFetch(
        `${apiBase}/api/projects/${activeProject}/git/log?${params}`
      );

      if (response.ok) {
        const data = await response.json();
        const fetchedCommits = data.commits || [];
        setCommits(fetchedCommits);
        return fetchedCommits;
      }
    } catch {
      // Return seed data on error
    } finally {
      setIsLoadingCommits(false);
    }

    // Generate seed data when server doesn't support git log
    const seedCommits = generateSeedCommits();
    setCommits(seedCommits);
    return seedCommits;
  }, [token, apiBase, activeProject, authFetch]);

  // Generate seed commit data for demo
  function generateSeedCommits(): GitCommit[] {
    const now = Date.now();
    const commits: GitCommit[] = [
      {
        hash: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
        shortHash: 'a1b2c3d',
        message: 'Add Peek Definition feature with Alt+F12 shortcut',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 5).toISOString(),
        parents: ['b1c2d3e'],
      },
      {
        hash: 'b1c2d3e4f5g6h7i8j9k0l1m2n3o4p5q6r7s8t9u0',
        shortHash: 'b1c2d3e',
        message: 'Implement Vim mode toggle via command palette',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 30).toISOString(),
        parents: ['c1d2e3f'],
      },
      {
        hash: 'c1d2e3f4g5h6i7j8k9l0m1n2o3p4q5r6s7t8u9v0',
        shortHash: 'c1d2e3f',
        message: 'Add multi-select files in explorer with Shift/Cmd+Click',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60).toISOString(),
        parents: ['d1e2f3g'],
      },
      {
        hash: 'd1e2f3g4h5i6j7k8l9m0n1o2p3q4r5s6t7u8v9w0',
        shortHash: 'd1e2f3g',
        message: 'Implement file decorations with git status icons',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 2).toISOString(),
        parents: ['e1f2g3h'],
      },
      {
        hash: 'e1f2g3h4i5j6k7l8m9n0o1p2q3r4s5t6u7v8w9x0',
        shortHash: 'e1f2g3h',
        message: 'Add file explorer drag & drop for moving files',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 3).toISOString(),
        parents: ['f1g2h3i'],
      },
      {
        hash: 'f1g2h3i4j5k6l7m8n9o0p1q2r3s4t5u6v7w8x9y0',
        shortHash: 'f1g2h3i',
        message: 'Implement branch management (create/delete/switch)',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 5).toISOString(),
        parents: ['g1h2i3j'],
      },
      {
        hash: 'g1h2i3j4k5l6m7n8o9p0q1r2s3t4u5v6w7x8y9z0',
        shortHash: 'g1h2i3j',
        message: 'Add Git push/pull/fetch functionality',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 24).toISOString(),
        parents: ['h1i2j3k'],
      },
      {
        hash: 'h1i2j3k4l5m6n7o8p9q0r1s2t3u4v5w6x7y8z9a0',
        shortHash: 'h1i2j3k',
        message: 'Implement side-by-side diff view modal',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 24 * 2).toISOString(),
        parents: ['i1j2k3l'],
      },
      {
        hash: 'i1j2k3l4m5n6o7p8q9r0s1t2u3v4w5x6y7z8a9b0',
        shortHash: 'i1j2k3l',
        message: 'Add Rename Symbol feature with F2 shortcut',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 24 * 3).toISOString(),
        parents: ['j1k2l3m'],
      },
      {
        hash: 'j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0',
        shortHash: 'j1k2l3m',
        message: 'Initial project setup with React and CodeMirror',
        author: 'Developer',
        email: 'dev@example.com',
        date: new Date(now - 1000 * 60 * 60 * 24 * 7).toISOString(),
        parents: [],
      },
    ];
    return commits;
  }

  // Fetch blame for a file
  const fetchBlame = useCallback(async (filePath: string): Promise<GitBlameLine[]> => {
    // Check if we already have blame data for this file
    if (blameData[filePath]) {
      return blameData[filePath];
    }

    setIsLoadingBlame(true);

    try {
      if (token && apiBase) {
        const response = await authFetch(
          `${apiBase}/api/projects/${activeProject}/git/blame/${encodeURIComponent(filePath)}`
        );

        if (response.ok) {
          const data = await response.json();
          const blame = data.blame || [];
          setBlameData(prev => ({ ...prev, [filePath]: blame }));
          return blame;
        }
      }
    } catch {
      // Fall through to generate seed data
    } finally {
      setIsLoadingBlame(false);
    }

    // Generate seed blame data
    const code = workingFiles[filePath] || persistedFiles[filePath] || '';
    const lines = code.split('\n');
    const seedBlame = generateSeedBlame(lines.length);
    setBlameData(prev => ({ ...prev, [filePath]: seedBlame }));
    return seedBlame;
  }, [token, apiBase, activeProject, authFetch, blameData, workingFiles, persistedFiles]);

  // Generate seed blame data for demo
  function generateSeedBlame(lineCount: number): GitBlameLine[] {
    const authors = ['Developer', 'Alice', 'Bob', 'Charlie'];
    const commits = [
      { hash: 'a1b2c3d', message: 'Add Peek Definition feature' },
      { hash: 'b1c2d3e', message: 'Implement Vim mode toggle' },
      { hash: 'c1d2e3f', message: 'Add multi-select files' },
      { hash: 'd1e2f3g', message: 'File decorations' },
      { hash: 'e1f2g3h', message: 'Drag & drop support' },
    ];
    const now = Date.now();

    const blame: GitBlameLine[] = [];
    let currentCommitIdx = 0;
    let chunkSize = Math.floor(Math.random() * 5) + 3;
    let chunkCount = 0;

    for (let i = 1; i <= lineCount; i++) {
      const commit = commits[currentCommitIdx];
      blame.push({
        lineNumber: i,
        hash: commit.hash + '0'.repeat(33),
        shortHash: commit.hash,
        author: authors[currentCommitIdx % authors.length],
        date: new Date(now - 1000 * 60 * 60 * 24 * (currentCommitIdx + 1)).toISOString(),
        message: commit.message,
      });

      chunkCount++;
      if (chunkCount >= chunkSize) {
        currentCommitIdx = (currentCommitIdx + 1) % commits.length;
        chunkSize = Math.floor(Math.random() * 5) + 3;
        chunkCount = 0;
      }
    }

    return blame;
  }

  // Clear blame data for a file (call when file is modified)
  const clearBlame = useCallback((filePath: string) => {
    setBlameData(prev => {
      const next = { ...prev };
      delete next[filePath];
      return next;
    });
  }, []);

  // View file content at a specific commit
  const viewFileAtCommit = useCallback(async (commitId: string, filePath: string): Promise<string | null> => {
    if (!token || !apiBase) {
      // Return simulated content when API not available
      const currentContent = workingFiles[filePath] || persistedFiles[filePath];
      if (!currentContent) return null;

      // Simulate a slightly different version based on commit
      const lines = currentContent.split('\n');
      const headerComment = `// Version at commit ${commitId.slice(0, 7)}\n// This is a simulated historical version\n\n`;
      return headerComment + lines.slice(0, Math.max(1, lines.length - 5)).join('\n');
    }

    try {
      const response = await authFetch(
        `${apiBase}/api/projects/${activeProject}/git/show/${commitId}/${encodeURIComponent(filePath)}`
      );

      if (response.ok) {
        const data = await response.json();
        return data.content ?? null;
      }

      // File might not exist in this commit
      return null;
    } catch {
      return null;
    }
  }, [token, apiBase, activeProject, authFetch, workingFiles, persistedFiles]);

  // Push to remote
  const push = useCallback(async (remote = 'origin', force = false): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    setIsPushing(true);
    setGitState(prev => ({ ...prev, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remote, force }),
      });

      if (response.ok) {
        setGitState(prev => ({ ...prev, ahead: 0 }));
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Push failed' }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Push failed: Network error' }));
      return false;
    } finally {
      setIsPushing(false);
    }
  }, [token, apiBase, activeProject, authFetch]);

  // Pull from remote
  const pull = useCallback(async (remote = 'origin', rebase = false): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    setIsPulling(true);
    setGitState(prev => ({ ...prev, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/pull`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remote, rebase }),
      });

      if (response.ok) {
        setGitState(prev => ({ ...prev, behind: 0 }));
        await fetchGitStatus();
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Pull failed' }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Pull failed: Network error' }));
      return false;
    } finally {
      setIsPulling(false);
    }
  }, [token, apiBase, activeProject, authFetch, fetchGitStatus]);

  // Fetch from remote
  const fetch = useCallback(async (remote = 'origin', prune = false): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    setIsFetching(true);
    setGitState(prev => ({ ...prev, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/fetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remote, prune }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({
          ...prev,
          ahead: data.ahead ?? prev.ahead,
          behind: data.behind ?? prev.behind,
        }));
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Fetch failed' }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Fetch failed: Network error' }));
      return false;
    } finally {
      setIsFetching(false);
    }
  }, [token, apiBase, activeProject, authFetch]);

  // Sync (pull then push)
  const sync = useCallback(async (): Promise<boolean> => {
    setIsSyncing(true);
    try {
      const pullResult = await pull();
      if (!pullResult) return false;
      const pushResult = await push();
      return pushResult;
    } finally {
      setIsSyncing(false);
    }
  }, [pull, push]);

  // Switch branch
  const switchBranch = useCallback(async (branchName: string): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    setGitState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ branch: branchName }),
      });

      if (response.ok) {
        setGitState(prev => ({
          ...prev,
          branch: branchName,
          branches: prev.branches.map(b => ({ ...b, current: b.name === branchName })),
          loading: false,
        }));
        await fetchGitStatus();
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Checkout failed', loading: false }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Checkout failed: Network error', loading: false }));
      return false;
    }
  }, [token, apiBase, activeProject, authFetch, fetchGitStatus]);

  // Create new branch
  const createBranch = useCallback(async (branchName: string, checkout = true): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    setGitState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/branch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: branchName, checkout }),
      });

      if (response.ok) {
        const newBranch: GitBranch = { name: branchName, current: checkout };
        setGitState(prev => ({
          ...prev,
          branch: checkout ? branchName : prev.branch,
          branches: checkout
            ? [...prev.branches.map(b => ({ ...b, current: false })), newBranch]
            : [...prev.branches, newBranch],
          loading: false,
        }));
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Create branch failed', loading: false }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Create branch failed: Network error', loading: false }));
      return false;
    }
  }, [token, apiBase, activeProject, authFetch]);

  // Delete branch
  const deleteBranch = useCallback(async (branchName: string, force = false): Promise<boolean> => {
    if (!token || !apiBase) {
      setGitState(prev => ({ ...prev, error: 'API not configured' }));
      return false;
    }

    if (branchName === gitState.branch) {
      setGitState(prev => ({ ...prev, error: 'Cannot delete current branch' }));
      return false;
    }

    setGitState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/branch/${encodeURIComponent(branchName)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });

      if (response.ok) {
        setGitState(prev => ({
          ...prev,
          branches: prev.branches.filter(b => b.name !== branchName),
          loading: false,
        }));
        return true;
      } else {
        const data = await response.json().catch(() => ({}));
        setGitState(prev => ({ ...prev, error: data.message || 'Delete branch failed', loading: false }));
        return false;
      }
    } catch (err) {
      setGitState(prev => ({ ...prev, error: 'Delete branch failed: Network error', loading: false }));
      return false;
    }
  }, [token, apiBase, activeProject, authFetch, gitState.branch]);

  // Stage a file
  const stageFile = useCallback((path: string) => {
    setGitState(prev => ({
      ...prev,
      files: prev.files.map(f =>
        f.path === path ? { ...f, staged: true } : f
      ),
    }));
  }, []);

  // Unstage a file
  const unstageFile = useCallback((path: string) => {
    setGitState(prev => ({
      ...prev,
      files: prev.files.map(f =>
        f.path === path ? { ...f, staged: false } : f
      ),
    }));
  }, []);

  // Stage all files
  const stageAll = useCallback(() => {
    setGitState(prev => ({
      ...prev,
      files: prev.files.map(f => ({ ...f, staged: true })),
    }));
  }, []);

  // Unstage all files
  const unstageAll = useCallback(() => {
    setGitState(prev => ({
      ...prev,
      files: prev.files.map(f => ({ ...f, staged: false })),
    }));
  }, []);

  // Discard changes in a file
  const discardFile = useCallback((path: string, onDiscard: (path: string, content: string) => void) => {
    const persisted = persistedFiles[path];
    if (persisted !== undefined) {
      onDiscard(path, persisted);
    }
  }, [persistedFiles]);

  // Commit staged files
  const commit = useCallback(async (onSuccess?: () => void) => {
    const stagedFiles = gitState.files.filter(f => f.staged);
    if (stagedFiles.length === 0 || !commitMessage.trim()) {
      return false;
    }

    setIsCommitting(true);

    try {
      if (token && apiBase) {
        // Try to commit via server
        const response = await authFetch(`${apiBase}/api/projects/${activeProject}/git/commit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: commitMessage,
            files: stagedFiles.map(f => f.path),
          }),
        });

        if (response.ok) {
          // Clear staged files after successful commit
          setGitState(prev => ({
            ...prev,
            files: prev.files.filter(f => !f.staged),
          }));
          setCommitMessage('');
          onSuccess?.();
          return true;
        }
      }

      // Local commit simulation (when server doesn't support git)
      // Just clear the staged files and reset commit message
      setGitState(prev => ({
        ...prev,
        files: prev.files.filter(f => !f.staged),
      }));
      setCommitMessage('');
      onSuccess?.();
      return true;
    } catch {
      return false;
    } finally {
      setIsCommitting(false);
    }
  }, [gitState.files, commitMessage, token, apiBase, activeProject, authFetch]);

  // Get file status icon
  const getStatusIcon = useCallback((status: GitFileStatus): string => {
    switch (status) {
      case 'modified': return 'M';
      case 'added': return 'A';
      case 'deleted': return 'D';
      case 'untracked': return 'U';
      case 'renamed': return 'R';
      default: return '?';
    }
  }, []);

  // Get file status color
  const getStatusColor = useCallback((status: GitFileStatus): string => {
    switch (status) {
      case 'modified': return 'var(--warning)';
      case 'added': return 'var(--success)';
      case 'deleted': return 'var(--danger)';
      case 'untracked': return 'var(--muted)';
      case 'renamed': return 'var(--info)';
      default: return 'var(--text)';
    }
  }, []);

  const stagedFiles = gitState.files.filter(f => f.staged);
  const unstagedFiles = gitState.files.filter(f => !f.staged);

  return {
    gitState,
    stagedFiles,
    unstagedFiles,
    commitMessage,
    setCommitMessage,
    isCommitting,
    isPushing,
    isPulling,
    isFetching,
    isSyncing,
    commits,
    isLoadingCommits,
    blameData,
    isLoadingBlame,
    fetchGitStatus,
    fetchBranches,
    fetchCommitLog,
    fetchBlame,
    clearBlame,
    viewFileAtCommit,
    stageFile,
    unstageFile,
    stageAll,
    unstageAll,
    discardFile,
    commit,
    push,
    pull,
    fetch,
    sync,
    switchBranch,
    createBranch,
    deleteBranch,
    getStatusIcon,
    getStatusColor,
  };
}
